import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, ChevronLeft, ChevronRight, CalendarDays, Video, Clock, List, History } from 'lucide-react';
import { resolveMediaUrl } from '@/utils/mediaUrl';

interface CalendarSession {
  productId: string;
  title: string;
  coverImage: string;
  price: number; // cents
  durationMins: number | null;
  creatorName: string;
  date: string; // ISO
  past?: boolean; // already happened — shown dimmed, and excluded from "upcoming"
  provider?: string | null; // partner-fed class (e.g. "In the Act Studios") — badged as theirs
  summary?: string;         // flattened description, for the list rows
  host?: string | null;     // standing host of the product
  hostRole?: string | null;
  guest?: string | null;    // guest booked for THIS occurrence, on top of the host
  guestRole?: string | null;
  // The same people as host/guest, one entry each, with their headshots — a show
  // that rotates hosts weekly sells on WHO is on, and a name in 12.5px grey was
  // not carrying that.
  hosts?: CalendarPresenter[];
  guests?: CalendarPresenter[];
  // A finished class whose host still answers replay questions within 24 hours:
  // it stays on the calendar as a replay until the window closes.
  activeReplay?: { until: string; creatorFirst: string } | null;
  hasReplay?: boolean; // a ready recording exists (the Past tab sells it)
  cohort?: boolean; // one enrollment covers every date (a multi-night course)
  // Upcoming list only: the dates this row stands for, soonest first (a course's
  // dates, or one day's time slots).
  dates?: string[];
}

type CalendarView = 'list' | 'past' | 'calendar';

// "Monday, November 2" for the replay window's last day.
const untilLabel = (iso: string) => new Date(iso).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

type CalendarPresenter = { name: string; role: string | null; image: string | null };

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Local YYYY-MM-DD key for grouping sessions into day cells.
const dayKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// "12pm" / "12:30pm" — compact chip time.
const chipTime = (d: Date) => {
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h >= 12 ? 'pm' : 'am';
  h = h % 12; if (h === 0) h = 12;
  return m === 0 ? `${h}${ap}` : `${h}:${String(m).padStart(2, '0')}${ap}`;
};

// The dates on a class's one row in the Upcoming list. Four slots on one Saturday
// read "Sat, Nov 7 · 12pm, 12:45pm, 1:30pm, 2:15pm"; a course over several days
// lists its days ("Wed Nov 4, Thu Nov 5, Mon Nov 9 +3 more · 6:30pm") rather
// than taking a row per night (Joe, Nov 4 2026).
const datesLabel = (isoDates: string[]) => {
  const ds = isoDates.map(x => new Date(x));
  const day = (d: Date) => d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  // No comma inside a day, so a list of days reads cleanly.
  const shortDay = (d: Date) => `${d.toLocaleDateString(undefined, { weekday: 'short' })} ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  const days: Date[] = [];
  for (const d of ds) if (!days.some(x => dayKey(x) === dayKey(d))) days.push(d);
  if (days.length === 1) return `${day(ds[0])} · ${ds.map(chipTime).join(', ')}`;
  const times = [...new Set(ds.map(chipTime))];
  const shown = days.slice(0, 3).map(shortDay).join(', ') + (days.length > 3 ? ` +${days.length - 3} more` : '');
  return times.length === 1 ? `${shown} · ${times[0]}` : shown;
};

const priceLabel = (cents: number) => (cents === 0 ? 'Free' : `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`);
// "480 min" is how a database says it, not how a person reads an all-day
// conference. Anything an hour or over reads in hours.
const durationLabel = (mins: number) => {
  if (!mins || mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
};

const WebinarCalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<CalendarSession[]>([]);
  const [loading, setLoading] = useState(true);
  // The month currently displayed (anchored to the 1st).
  // List or month view. The month grid used to sit below the whole upcoming list,
  // where nobody found it; now it's a toggle at the top. Kept in ?view= so a
  // shared link opens the same view.
  const [params, setParams] = useSearchParams();
  const view: CalendarView = params.get('view') === 'calendar' ? 'calendar' : params.get('view') === 'past' ? 'past' : 'list';
  const setView = (v: CalendarView) => {
    const next = new URLSearchParams(params);
    if (v === 'list') next.delete('view'); else next.set('view', v);
    setParams(next, { replace: true });
  };
  const [cursor, setCursor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });

  useEffect(() => {
    let cancelled = false;
    fetch('/api/marketplace/webinar-calendar')
      .then(r => r.json())
      .then(d => { if (!cancelled) setSessions(Array.isArray(d?.sessions) ? d.sessions : []); })
      .catch(() => { if (!cancelled) setSessions([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Group sessions by local day key.
  const byDay = useMemo(() => {
    const map = new Map<string, CalendarSession[]>();
    for (const s of sessions) {
      const d = new Date(s.date);
      if (isNaN(d.getTime())) continue;
      const k = dayKey(d);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(s);
    }
    for (const list of map.values()) list.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    return map;
  }, [sessions]);

  // Every upcoming session across all months, soonest first (no cap: the list
  // IS the schedule, and the feed already stops six months out).
  const upcoming = useMemo(() => {
    const now = Date.now();
    const ahead = sessions
      .filter(s => !s.past && new Date(s.date).getTime() >= now)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    // A course that's one enrollment shows once, at its next date, with its other
    // dates on the row; a class's time slots on one day share a row. A weekly series
    // keeps a row per date: each one is its own event (Joe, Nov 4 2026).
    const rowsByKey = new Map<string, CalendarSession>();
    for (const s of ahead) {
      const key = s.cohort ? s.productId : `${s.productId}|${dayKey(new Date(s.date))}`;
      const row = rowsByKey.get(key);
      if (row) row.dates!.push(s.date);
      else rowsByKey.set(key, { ...s, dates: [s.date] });
    }
    const live = [...rowsByKey.values()];
    // Active replays lead the list (Joe: "it's the hot thing, and there would only
    // ever be one or two"), the one closing soonest first, capped at five so a
    // busy week can't push the live schedule below the fold.
    const replays = sessions
      .filter(s => s.activeReplay && new Date(s.activeReplay.until).getTime() > now)
      .sort((a, b) => new Date(a.activeReplay!.until).getTime() - new Date(b.activeReplay!.until).getTime())
      .slice(0, 5);
    return [...replays, ...live];
  }, [sessions]);
  const replayCount = upcoming.filter(s => s.activeReplay).length;
  // The Past tab: everything that already happened in the feed's lookback, newest
  // first. Proof that things happen here, and the replays are for sale.
  const past = useMemo(() => {
    const now = Date.now();
    return sessions
      .filter(s => s.past || new Date(s.date).getTime() < now)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [sessions]);
  const rows = view === 'past' ? past : upcoming;

  // Build the day grid: pad from the Sunday before the 1st to the Saturday after the last.
  const grid = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const startOffset = first.getDay(); // 0 = Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;
    const cells: { date: Date; inMonth: boolean }[] = [];
    for (let i = 0; i < totalCells; i++) {
      const date = new Date(year, month, 1 - startOffset + i);
      cells.push({ date, inMonth: date.getMonth() === month });
    }
    return cells;
  }, [cursor]);

  const todayKey = dayKey(new Date());
  const monthLabel = cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const goPrev = () => setCursor(c => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const goNext = () => setCursor(c => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  const goToday = () => { const n = new Date(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); };

  // Chips wrap onto a second line but never a third. The -webkit-line-clamp
  // combo can't live on the <button> itself (Chrome's button-internal layout
  // ignores it), so the button is just the pill and an inner span carries the
  // clamp; a character cap is the hard backstop.
  const chipBtn: React.CSSProperties = {
    display: 'block', width: '100%', textAlign: 'left', padding: '4px 6px', borderRadius: 6,
    background: 'var(--hj-blue)', border: 0, color: 'oklch(0.18 0.005 70)', fontSize: 11, fontWeight: 600,
    fontFamily: 'inherit', cursor: 'pointer', overflow: 'hidden',
  };
  const chipClamp: React.CSSProperties = {
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
    overflow: 'hidden', overflowWrap: 'anywhere', lineHeight: 1.35,
  };
  const chipTitle = (t: string) => (t.length > 44 ? t.slice(0, 43).trimEnd() + '…' : t);

  return (
    <main style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)', minHeight: 'calc(100vh - 68px)' }}>
      <style>{`
        .hj-cal-grid-wrap { overflow-x: auto; }
        .hj-cal-grid { min-width: 1050px; }
        .hj-cal-row:hover { background: var(--hj-beige-hover, rgba(0,0,0,0.03)) !important; }
        /* Two lines max, so a long description can't make one row tower over the rest. */
        .hj-cal-summary {
          display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
          overflow: hidden;
        }
        /* The title is what tells one session from another, so it wraps to two
           lines rather than truncating. It used to be a single truncated line,
           which on a phone meant every row read "Diving Into th…". */
        .hj-cal-title {
          display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
          overflow: hidden; overflow-wrap: anywhere;
        }
        .hj-cal-price-inline { display: none; }
        @media (max-width: 640px) {
          .hj-cal-summary { -webkit-line-clamp: 3; }
          /* A 132px thumbnail beside a price column left about 200px for text on a
             phone, so every title truncated. Stack instead: the banner goes full
             width on top, the price folds into the date line, and the text gets
             the whole screen. */
          .hj-cal-row {
            flex-direction: column !important; align-items: stretch !important;
            gap: 10px !important; padding: 16px 2px !important;
          }
          .hj-cal-thumb { width: 100% !important; height: auto !important; aspect-ratio: 16 / 9; }
          .hj-cal-price-col { display: none !important; }
          .hj-cal-price-inline { display: inline-block; margin-left: auto; font-weight: 800; }
          .hj-cal-meta { width: 100%; }
        }
      `}</style>
      <div className="mx-auto" style={{ maxWidth: 1120, padding: '0 24px 96px' }}>
        {/* Breadcrumb */}
        <div className="flex items-center gap-2.5" style={{ padding: '20px 0 10px', fontSize: 13 }}>
          <Link to="/marketplace" className="inline-flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}>
            <ChevronLeft size={12} strokeWidth={2} />
            Marketplace
          </Link>
          <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
          <span style={{ color: 'var(--hj-ink)' }}>Featured events</span>
        </div>

        <header style={{ margin: '4px 0 24px', padding: '22px 24px 20px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)' }}>
          <div className="mono-label" style={{ marginBottom: 10, color: 'var(--hj-beige-ink-2)', fontSize: 12, letterSpacing: '0.12em' }}>Live sessions</div>
          <h1 className="font-hj-display m-0" style={{ fontSize: 'clamp(32px, 4.2vw, 50px)', lineHeight: 1, color: 'var(--hj-beige-ink)', letterSpacing: '-0.025em' }}>
            Featured event &amp; class calendar
          </h1>
          <p className="m-0" style={{ marginTop: 12, color: 'var(--hj-beige-ink)', fontSize: 15.5, maxWidth: 600, lineHeight: 1.5 }}>
            Upcoming live classes and featured event listings
          </p>
        </header>

        {loading ? (
          <div className="flex items-center justify-center" style={{ padding: '80px 0' }}>
            <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-3)' }} />
          </div>
        ) : sessions.length === 0 ? (
          <div className="flex flex-col items-center text-center" style={{ padding: '64px 24px' }}>
            <span className="inline-flex items-center justify-center" style={{ width: 56, height: 56, borderRadius: 999, background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-3)', marginBottom: 16 }}>
              <CalendarDays className="w-6 h-6" />
            </span>
            <h2 className="font-hj-display m-0" style={{ fontSize: 22, color: 'var(--hj-ink)', letterSpacing: '-0.02em' }}>No featured events or classes on the calendar yet</h2>
            <p className="m-0" style={{ marginTop: 8, fontSize: 14.5, color: 'var(--hj-ink-2)' }}>Check back soon.</p>
          </div>
        ) : (
          <>
            <div role="tablist" aria-label="Calendar view" className="inline-flex" style={{ marginBottom: 20, padding: 4, borderRadius: 999, background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)' }}>
              {([['list', 'Upcoming', List], ['past', 'Past', History], ['calendar', 'Calendar', CalendarDays]] as const).map(([key, label, Icon]) => {
                const on = view === key;
                return (
                  <button key={key} type="button" role="tab" aria-selected={on} onClick={() => setView(key)}
                    className="inline-flex items-center gap-2"
                    style={{ padding: '8px 16px', borderRadius: 999, border: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
                      background: on ? 'var(--hj-ink)' : 'transparent', color: on ? 'var(--hj-surface-1)' : 'var(--hj-ink)' }}>
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                );
              })}
            </div>

            {/* Upcoming list. Most people come here to see what's next, so it's
                the default view; the month grid is the other tab. */}
            {(view === 'list' || view === 'past') && (
            <div style={{ marginBottom: 40 }}>
              <div className="flex items-baseline justify-between" style={{ marginBottom: 14 }}>
                <h2 className="font-hj-display m-0" style={{ fontSize: 24, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}>{view === 'past' ? 'Past' : 'Upcoming'}</h2>
                {rows.length > 0 && (
                  <span className="mono-label" style={{ color: 'var(--hj-ink-3)', fontSize: 12 }}>
                    {view === 'past'
                      ? `${rows.length} session${rows.length === 1 ? '' : 's'} · ${rows.filter(s => s.hasReplay).length} with a replay`
                      : `${rows.length - replayCount} class${rows.length - replayCount === 1 ? '' : 'es'}${replayCount > 0 ? ` · ${replayCount} active replay${replayCount === 1 ? '' : 's'}` : ''}`}
                  </span>
                )}
              </div>
              {rows.length === 0 ? (
                <p className="m-0" style={{ fontSize: 14, color: 'var(--hj-ink-2)' }}>{view === 'past' ? 'Nothing in the last few months yet.' : 'No upcoming sessions this far out.'}</p>
              ) : (
                <div style={{ borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', overflow: 'hidden' }}>
                  {rows.map((s2, i) => {
                    const d = new Date(s2.date);
                    const free = s2.price === 0;
                    const cover = resolveMediaUrl(s2.coverImage);
                    const dateStr = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
                    const replay = s2.activeReplay || null;
                    const held = view === 'past' && !replay; // a plain past row: "Held …", sells the recording if there is one
                    return (
                      <button
                        key={s2.productId + i}
                        type="button"
                        onClick={() => navigate(`/course/${s2.productId}`)}
                        className="hj-cal-row w-full flex items-start gap-4"
                        style={{
                          padding: '16px 18px',
                          borderTop: i === 0 ? 'none' : '1px solid var(--hj-beige-line)',
                          // A replay row sits on a yellow tint with a yellow edge, so it reads
                          // as a different kind of thing from a seat you book.
                          background: replay ? 'var(--hj-yellow-tint)' : 'transparent',
                          borderLeft: replay ? '4px solid var(--hj-yellow)' : 0, borderRight: 0, borderBottom: 0,
                          cursor: 'pointer', textAlign: 'left',
                        }}
                      >
                        {/* The class banner, not a generic icon — these are the only
                            thing that tells one session apart from another at a glance. */}
                        <span
                          className="hj-cal-thumb flex-shrink-0 inline-flex items-center justify-center overflow-hidden"
                          style={{ width: 132, height: 76, borderRadius: 10, background: 'var(--hj-beige-deep)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-3)' }}
                        >
                          {cover
                            ? <img src={cover} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                            : <Video className="w-5 h-5" />}
                        </span>

                        <span className="flex-1 min-w-0">
                          <span className="hj-cal-meta flex items-center gap-2 flex-wrap" style={{ marginBottom: 3 }}>
                            {/* Full-contrast ink, NOT an accent. --hj-blue is aliased
                                to the brand yellow (#ffd600), so a "blue" date line
                                renders yellow-on-cream and is unreadable — the
                                #2563EB fallback never applies, because the variable
                                is defined. The date is primary content here. */}
                            {replay ? (
                              <>
                                <span style={{ padding: '2px 9px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>Active replay</span>
                                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--hj-beige-ink)' }}>Replay and Q&amp;A open until {untilLabel(replay.until)}</span>
                              </>
                            ) : (
                              <span className="mono-label" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--hj-beige-ink)', letterSpacing: '0.06em', whiteSpace: s2.dates && s2.dates.length > 1 ? 'normal' : 'nowrap' }}>
                                {held ? 'Held ' : ''}{s2.dates && s2.dates.length > 1 ? datesLabel(s2.dates) : `${dateStr} · ${chipTime(d)}`}
                              </span>
                            )}
                            {s2.durationMins ? (
                              <span className="inline-flex items-center gap-1" style={{ fontSize: 11.5, color: 'var(--hj-beige-ink-3)' }}>
                                <Clock className="w-3 h-3" /> {durationLabel(s2.durationMins)}
                              </span>
                            ) : null}
                            {s2.provider ? (
                              <span className="mono-label" style={{ padding: '1px 6px', borderRadius: 4, background: 'var(--hj-beige-deep)', color: 'var(--hj-beige-ink-2)', fontSize: 10, border: '1px solid var(--hj-beige-line)' }}>{s2.provider}</span>
                            ) : null}
                            {/* Phone copy of the price. The right-hand column is
                                hidden at this width; aria-hidden so it isn't read twice. */}
                            <span aria-hidden="true" className="hj-cal-price-inline" style={{ fontSize: 13.5, color: free ? 'var(--hj-beige-ink-2)' : 'var(--hj-beige-ink)' }}>{priceLabel(s2.price)}</span>
                          </span>

                          <span className="hj-cal-title block" style={{ fontSize: 16, fontWeight: 700, color: 'var(--hj-beige-ink)', letterSpacing: '-0.01em', lineHeight: 1.25 }}>{s2.title}</span>

                          {(s2.host || s2.creatorName) && (
                            <span className="flex items-center gap-2" style={{ marginTop: 3 }}>
                              {/* Faces first. A recurring series keeps its standing host and
                                  rotates the guest, so both sets ride the same line rather
                                  than the guest replacing the host. */}
                              {(() => {
                                const faces = [...(s2.hosts || []), ...(s2.guests || [])].filter(h => h.image).slice(0, 3);
                                if (!faces.length) return null;
                                return (
                                  <span className="flex flex-shrink-0">
                                    {faces.map((h, fi) => (
                                      <img
                                        key={`${h.name}-${fi}`}
                                        src={resolveMediaUrl(h.image!)}
                                        alt=""
                                        className="hj-headshot"
                                        style={{ width: 28, height: 28, marginLeft: fi ? -7 : 0, boxShadow: '0 0 0 2px var(--hj-beige)' }}
                                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                      />
                                    ))}
                                  </span>
                                );
                              })()}
                              <span style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)', minWidth: 0 }}>
                                {s2.hostRole ? `${s2.hostRole}: ` : 'with '}{s2.host || s2.creatorName}
                                {s2.guest ? ` · ${s2.guestRole ? `${s2.guestRole}: ` : 'guest '}${s2.guest}` : ''}
                              </span>
                            </span>
                          )}

                          {replay ? (
                            <span className="block" style={{ marginTop: 5, fontSize: 13, lineHeight: 1.45, color: 'var(--hj-beige-ink)' }}>
                              Held {dateStr}. Watch the recording and ask {replay.creatorFirst} anything about it. Every question is answered within 24 hours.
                            </span>
                          ) : s2.summary ? (
                            <span className="hj-cal-summary block" style={{ marginTop: 5, fontSize: 13, lineHeight: 1.45, color: 'var(--hj-beige-ink-2)' }}>{s2.summary}</span>
                          ) : null}
                        </span>

                        <span className="hj-cal-price-col flex-shrink-0 text-right">
                          <span className="block" style={{ fontSize: 15, fontWeight: 800, color: free ? 'var(--hj-beige-ink-2)' : 'var(--hj-beige-ink)' }}>{priceLabel(s2.price)}</span>
                          {(replay || (held && s2.hasReplay)) && <span className="block" style={{ marginTop: 4, fontSize: 12, fontWeight: 700, color: 'var(--hj-beige-ink)' }}>Watch the replay</span>}
                          {held && !s2.hasReplay && <span className="block" style={{ marginTop: 4, fontSize: 12, fontWeight: 600, color: 'var(--hj-beige-ink-2)' }}>No recording</span>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            )}

            {view === 'calendar' && (<>
            {/* Month controls */}
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
              <h2 className="font-hj-display m-0" style={{ fontSize: 22, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}>{monthLabel}</h2>
              <div className="flex items-center gap-2">
                <button type="button" onClick={goToday} style={{ padding: '7px 14px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>Today</button>
                <button type="button" onClick={goPrev} aria-label="Previous month" className="inline-flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', cursor: 'pointer' }}>
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button type="button" onClick={goNext} aria-label="Next month" className="inline-flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', cursor: 'pointer' }}>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Month grid */}
            <div className="hj-cal-grid-wrap">
              <div className="hj-cal-grid" style={{ border: '1px solid var(--hj-beige-line)', borderRadius: 'var(--hj-r-md)', overflow: 'hidden' }}>
                {/* Weekday header */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(150px, 1fr))', background: 'var(--hj-beige-deep)' }}>
                  {WEEKDAYS.map(w => (
                    <div key={w} className="mono-label" style={{ padding: '9px 8px', textAlign: 'center', color: 'var(--hj-beige-ink-2)', borderBottom: '1px solid var(--hj-beige-line)' }}>{w}</div>
                  ))}
                </div>
                {/* Day cells */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(150px, 1fr))' }}>
                  {grid.map((cell, i) => {
                    const k = dayKey(cell.date);
                    const isToday = k === todayKey;
                    const daySessions = byDay.get(k) || [];
                    return (
                      <div
                        key={i}
                        style={{
                          minHeight: 104,
                          padding: 6,
                          borderRight: (i % 7 !== 6) ? '1px solid var(--hj-beige-line)' : 'none',
                          borderTop: i >= 7 ? '1px solid var(--hj-beige-line)' : 'none',
                          background: cell.inMonth ? 'var(--hj-beige)' : 'var(--hj-beige-deep)',
                        }}
                      >
                        <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
                          <span
                            className="inline-flex items-center justify-center"
                            style={{
                              width: 22, height: 22, borderRadius: 999, fontSize: 12, fontWeight: 700,
                              background: isToday ? 'var(--hj-yellow)' : 'transparent',
                              color: isToday ? 'oklch(0.18 0.005 70)' : (cell.inMonth ? 'var(--hj-beige-ink-2)' : 'var(--hj-beige-ink-3)'),
                            }}
                          >
                            {cell.date.getDate()}
                          </span>
                        </div>
                        <div className="flex flex-col gap-1">
                          {daySessions.map((s, si) => {
                            const d = new Date(s.date);
                            const free = s.price === 0;
                            return (
                              <button
                                key={s.productId + si}
                                type="button"
                                title={`${s.title} · ${chipTime(d)}${s.provider ? ` · presented by ${s.provider}` : ''}${free ? ' · free' : ''}${s.activeReplay ? ` · active replay until ${untilLabel(s.activeReplay.until)}` : s.past ? ' · already happened' : ''}`}
                                onClick={() => navigate(`/course/${s.productId}`)}
                                // A finished class with an active replay keeps full strength: it is still on.
                                style={s.past && !s.activeReplay ? { ...chipBtn, opacity: 0.55 } : chipBtn}
                              >
                                <span style={chipClamp}>
                                  {chipTime(d)} {chipTitle(s.title)}
                                  {s.provider && <span className="mono-label" style={{ marginLeft: 4, padding: '0 4px', borderRadius: 4, background: 'var(--hj-beige-deep)', color: 'var(--hj-beige-ink-2)', fontSize: 9, border: '1px solid var(--hj-beige-line)' }}>{s.provider}</span>}
                                  {free && !s.past && <span className="mono-label" style={{ marginLeft: 4, padding: '0 4px', borderRadius: 4, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 9 }}>free</span>}
                                  {s.activeReplay && <span className="mono-label" style={{ marginLeft: 4, padding: '0 4px', borderRadius: 4, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 9 }}>replay</span>}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            </>)}

          </>
        )}
      </div>
    </main>
  );
};

export default WebinarCalendarPage;
